import SkeletonTable from '@/components/SkeletonTable'
import { AuditLogRow } from '@/features/AuditLogs/components/AuditLogsTable/AuditLogRow'
import { groupLogsByDate } from '@/features/AuditLogs/utils/auditLogUtils'
import type { AuditLogs } from '@/generated/http-clients/v3ApiSchemas'
import { Table, Text } from '@chakra-ui/react'
import { Fragment, type ReactNode, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface AuditLogsTableProps {
  logs: AuditLogs[]
  isLoading: boolean
  isError: boolean
  isEmpty: boolean
  skeletonWidths: number[]
  selectedLogId?: string
  onRowClick: (log: AuditLogs) => void
  errorComponent: ReactNode
  emptyMessage: string
}

export function AuditLogsTable({
  logs,
  isLoading,
  isError,
  isEmpty,
  skeletonWidths,
  selectedLogId,
  onRowClick,
  errorComponent,
  emptyMessage,
}: Readonly<AuditLogsTableProps>) {
  const { t } = useTranslation('features', { useSuspense: false })

  // Group logs by date
  const groupedLogs = useMemo(() => groupLogsByDate(logs), [logs])
  const hasMultipleDates = groupedLogs.length > 1

  // Column count for colSpan (status indicator + timestamp + user + resource)
  const columnCount = 4

  return (
    <Table.Root size="sm" width="100%" aria-label="Audit logs table">
      <Table.Header>
        <Table.Row>
          {/* Status indicator column - no header */}
          <Table.ColumnHeader width="4px" p={0} />
          <Table.ColumnHeader whiteSpace="nowrap" width="auto">
            {t('auditLogs.table.columns.timestamp')}
          </Table.ColumnHeader>
          <Table.ColumnHeader width="220px">
            {t('auditLogs.table.columns.user')}
          </Table.ColumnHeader>
          <Table.ColumnHeader>
            {t('auditLogs.table.columns.resource')}
          </Table.ColumnHeader>
        </Table.Row>
      </Table.Header>
      <Table.Body>
        {isLoading && <SkeletonTable skeletonWidths={skeletonWidths} />}

        {isError && (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>{errorComponent}</Table.Cell>
          </Table.Row>
        )}

        {isEmpty && !isLoading && !isError && (
          <Table.Row>
            <Table.Cell colSpan={columnCount}>
              <Text color="gray.500" py={4} textAlign="center">
                {emptyMessage}
              </Text>
            </Table.Cell>
          </Table.Row>
        )}

        {!isLoading &&
          !isError &&
          !isEmpty &&
          groupedLogs.map((group) => (
            <Fragment key={group.date}>
              {/* Date header row - only show if multiple dates */}
              {hasMultipleDates && (
                <Table.Row bg="gray.50" _hover={{ bg: 'gray.50' }}>
                  <Table.Cell colSpan={columnCount} py={2}>
                    <Text
                      fontSize="xs"
                      fontWeight="semibold"
                      color="gray.600"
                      textTransform="uppercase"
                      letterSpacing="wide"
                    >
                      {group.date}
                    </Text>
                  </Table.Cell>
                </Table.Row>
              )}
              {/* Log rows */}
              {group.logs.map((log) => (
                <AuditLogRow
                  key={log.id}
                  log={log}
                  isSelected={log.id === selectedLogId}
                  onClick={() => onRowClick(log)}
                  showDate={!hasMultipleDates}
                />
              ))}
            </Fragment>
          ))}
      </Table.Body>
    </Table.Root>
  )
}
